import React, {
  useState,
  useRef,
  useEffect,
  // useCallback
} from 'react';
import { CloseButtonIcon, SendButtonIcon } from './icons/ChatIcons';
import Logo from './icons/Logo';
import LanguageSwitcher from './LanguageSwitcher';
import {
  getChatContentStyle,
  getHeaderStyle,
  getCloseButtonStyle,
  getMessagesContainerStyle,
  getEmptyStateStyle,
  getBotMessageStyle,
  getUserMessageStyle,
  getMessageContentStyle,
  getInputContainerStyle,
  getInputStyle,
  getInputFocusStyle,
  getSendButtonStyle,
  getSendButtonHoverStyle,
  getCarouselContainerStyle,
  getCarouselItemStyle,
  getCarouselNavButtonStyle,
} from './styles/ChatStyles';
import { ChatMessage } from './types/ChatTypes';
import { createTranslator, Language } from '../lib/i18n';
import echo from '../lib/echo';
import './website/universal-add-to-cart';
import { addProductsToWebsiteCart } from './website/universal-add-to-cart';

// Function to safely render HTML content
const renderMessageContent = (content: string, type?: string) => {
  // Default to treating content as plain text
  if (!type || type === 'String') {
    return <div>{content}</div>;
  }

  // Handle HTML content
  if (type === 'HTML') {
    // For HTML content with error messages
    if (content.startsWith('Eroare:') || content.includes('Error:') || content.includes('Traceback') || content.includes('KeyError:')) {
      return <div className="error-message">{content}</div>;
    }

    // For JSON streaming format - handle it more gracefully
    if (content.includes('{"chunk":')) {
      try {
        // Try to extract and combine all chunks
        const jsonChunks = content
          .split('\n')
          .filter((line) => line.trim())
          .map((line) => {
            try {
              return JSON.parse(line);
            } catch (e) {
              return { chunk: line, final: false };
            }
          });

        // Combine all chunks into one content string
        const combinedContent = jsonChunks.map((chunk) => chunk.chunk || '').join('');

        // If it looks like narrative text, format it nicely
        if (combinedContent.length > 100 && !combinedContent.includes('<')) {
          // Format as paragraphs
          const paragraphs = combinedContent
            .split('\n\n')
            .filter((p) => p.trim())
            .map((p, i) => <p key={i}>{p}</p>);

          return <div className="story-content">{paragraphs}</div>;
        }

        // Otherwise render as HTML if it contains HTML tags
        if (combinedContent.includes('<')) {
          return <div dangerouslySetInnerHTML={{ __html: combinedContent }} />;
        }

        // Or as simple text
        return <div>{combinedContent}</div>;
      } catch (e) {
        // If parsing fails, just show as an error
        console.error('Error processing JSON chunks:', e);
        return <div className="error-message">{content}</div>;
      }
    }

    // Regular HTML content
    return <div dangerouslySetInnerHTML={{ __html: content }} />;
  }

  // Fallback for any other types
  return <div>{content}</div>;
};

interface ChatContentProps {
  messages: ChatMessage[];
  setMessages: (messages: ChatMessage[]) => void;
  onClose: () => void;
  onSendMessage?: (message: string) => void;
  style: any;
  chatGroups: Array<{
    id: number;
    title?: string;
    conversation_id: number;
  }>;
  activeChatGroupId?: number | null;
  onChatGroupChange?: (groupId: number) => void;
  conversationId?: number;
  setConversationId: (id: number) => void;
  onLoadMoreChats?: () => Promise<boolean>; // New prop for lazy loading
  userId: string;
  websiteName: string;
  toastMessage: string | null;
  setToastMessage: (message: string | null) => void;
  showToast: boolean;
  setShowToast: (show: boolean) => void;
  langCode: Language;
  supportedLanguages?: string; // Comma-separated language codes
  onLanguageChange?: (language: Language) => void;
}

const ChatContent: React.FC<ChatContentProps> = ({
  messages,
  setMessages,
  onClose,
  onSendMessage,
  style,
  chatGroups,
  activeChatGroupId,
  onChatGroupChange,
  conversationId,
  setConversationId,
  onLoadMoreChats,
  userId,
  websiteName,
  toastMessage,
  setToastMessage,
  showToast,
  setShowToast,
  langCode,
  supportedLanguages,
  onLanguageChange,
}) => {
  // Helper function to get loading GIF URL from chat settings
  const getLoadingGifUrl = (): string | undefined => {
    try {
      const chatSettings = window.CreativeOnesChatbot?.chatSettings;
      if (chatSettings && chatSettings.success && Array.isArray(chatSettings.settings)) {
        const loadingGifSetting = chatSettings.settings.find((setting: any) => setting.slug === 'loading_gif_image' && setting.is_active);
        return loadingGifSetting?.value || undefined;
      }
      return undefined;
    } catch (error) {
      console.error('Error getting loading GIF URL:', error);
      return undefined;
    }
  };

  // Helper function to get loading GIF URL from chat settings
  const getBotImageUrl = (): string | undefined => {
    try {
      const chatSettings = window.CreativeOnesChatbot?.chatSettings;
      if (chatSettings && chatSettings.success && Array.isArray(chatSettings.settings)) {
        const loadingGifSetting = chatSettings.settings.find((setting: any) => setting.slug === 'bot_user_image' && setting.is_active);
        return loadingGifSetting?.value || undefined;
      }
      return undefined;
    } catch (error) {
      console.error('Error getting bot image URL:', error);
      return undefined;
    }
  };

  // Helper function to get loading GIF URL from chat settings
  const getUserImageUrl = (): string | undefined => {
    try {
      const chatSettings = window.CreativeOnesChatbot?.chatSettings;
      if (chatSettings && chatSettings.success && Array.isArray(chatSettings.settings)) {
        const loadingGifSetting = chatSettings.settings.find((setting: any) => setting.slug === 'guest_user_image' && setting.is_active);
        return loadingGifSetting?.value || undefined;
      }
      return undefined;
    } catch (error) {
      console.error('Error getting loading GIF URL:', error);
      return undefined;
    }
  };

  const getMagnifyingGlassUrl = (): string | undefined => {
    try {
      const chatSettings = window.CreativeOnesChatbot?.chatSettings;
      if (chatSettings && chatSettings.success && Array.isArray(chatSettings.settings)) {
        const magnifyingGlassSetting = chatSettings.settings.find((setting: any) => setting.slug === 'magnifying_glass_gif_image' && setting.is_active);
        return magnifyingGlassSetting?.value || undefined;
      }
      return undefined;
    } catch (error) {
      console.error('Error getting magnifying glass URL:', error);
      return undefined;
    }
  };

  // Create translation function for this component
  const t = createTranslator(langCode);
  const [inputValue, setInputValue] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isSendButtonHovered, setIsSendButtonHovered] = useState(false);
  const [currentChatGroupId, setCurrentChatGroupId] = useState<number | null>(activeChatGroupId || chatGroups[0]?.id || null);
  const [isAiTyping, setIsAiTyping] = useState(false);
  const [streamedResponse, setStreamedResponse] = useState('');
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [canContinueConversation, setCanContinueConversation] = useState(false);
  const [isCheckingContinuation, setIsCheckingContinuation] = useState(false);
  const [isAlreadyStreaming, setIsAlreadyStreaming] = useState(false);
  const [currentThinkingStep, setCurrentThinkingStep] = useState<string>('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const sendButtonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const continueConversationButtonRef = useRef<HTMLButtonElement>(null);
  const isAiStreamingRef = useRef<HTMLDivElement>(null);

  // Array of loading messages to cycle through (fallback when no thinking step)
  const loadingMessages = [
    t('collectingInfo'),
    t('searchingBestResults'),
    t('analyzingDatabase'),
    t('checkingSpecialOffers'),
    t('searchingSimilarProducts'),
    t('preparingRecommendations'),
    t('checkingStock'),
    t('finalizingResponse'),
  ];

  // Function to get message based on thinking step chunk
  const getThinkingStepMessage = (chunk: string): string => {
    // Handle emoji-based thinking steps
    if (chunk.includes('🔄 Starting though chain')) {
      return t('analyzingRequest');
    }
    if (chunk.includes('🔍 Creating or retriving conversation data')) {
      return t('analyzingRequest');
    }
    if (chunk.includes('🛒 Check and save user provided personal information')) {
      return t('analyzingRequest');
    }
    if (chunk.includes('🛒 Checking if user wants to add products to cart')) {
      return t('analyzingRequest');
    }
    if (chunk.includes('🔍 Collecting informations')) {
      return t('collectingInfo');
    }
    if (chunk.includes('🔍 Clearing Redis IDs')) {
      return t('collectingInfo');
    }
    if (chunk.includes('🪓 Breaking down into segments')) {
      return t('generatingSearchTerms');
    }
    if (chunk.includes('🔍 Generating search queries')) {
      return t('processingSearchTerms');
    }
    if (chunk.includes('🔠 Tokenizing search queries')) {
      return t('processingSearchTerms');
    }
    if (chunk.includes('🔎 Searching database using tokens')) {
      return t('searchingDatabase');
    }
    if (chunk.includes('🧩 Processing Redis IDs')) {
      return t('processingResults');
    }
    if (chunk.includes('💰 Generating time and cost estimates')) {
      return t('generatingEstimates');
    }
    if (chunk.includes('📝 Creating final response')) {
      return t('generatingFinalResponse');
    }

    // Default fallback
    return t('processingRequest');
  };

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (isAlreadyStreaming) {
      isAiStreamingRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else if (canContinueConversation) {
      continueConversationButtonRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, streamedResponse, canContinueConversation, isAlreadyStreaming]);

  useEffect(() => {
    async function checkStreamStatus() {
      try {
        if (isAiTyping) return;
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/check-stream-status`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_unique_identifier: userId,
            conversation_id: conversationId,
            lang_code: langCode,
          }),
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await response.json();
        const isStreaming = Boolean(data.is_streaming);

        // If is already streaming and the is streaming is false, refetch the messages
        if (isAlreadyStreaming && !isStreaming) {
          //@ts-ignore
          onChatGroupChange && onChatGroupChange(activeChatGroupId);

          setIsAlreadyStreaming(isStreaming);
        } else if (!isAlreadyStreaming && isStreaming) {
          setIsAlreadyStreaming(isStreaming);
        }
      } catch (error) {
        console.error('Error checking stream status:', error);
      }
    }

    checkStreamStatus();

    const streamStatusCheckInterval = setInterval(() => {
      checkStreamStatus();
    }, 3000);

    // Clean up interval and event listeners on component unmount
    return () => {
      clearInterval(streamStatusCheckInterval);
    };
  }, [isAlreadyStreaming, conversationId, userId, activeChatGroupId, onChatGroupChange, isAiTyping, langCode]);

  useEffect(() => {
    if (activeChatGroupId && activeChatGroupId !== currentChatGroupId) {
      setCurrentChatGroupId(activeChatGroupId);
    }
  }, [activeChatGroupId, currentChatGroupId]);

  // Handle cart button clicks using event delegation
  const handleCartButtonClick = (event: Event) => {
    const target = event.target as HTMLElement;

    // Check if clicked element is a cart button or inside one
    const button = target.closest('.findly-add-to-cart') as HTMLElement;
    if (!button) return;

    const productCode: string | null = button.getAttribute('data-product-code');
    const quantity = 1;

    if (productCode) {
      addProductsToFindlyCart(productCode, quantity);
      try {
        addProductsToWebsiteCart(productCode, quantity, null);
      } catch (error) {
        console.error('Error adding product to OpenCartV1 cart:', error);
      }

      // Show success toast notification
      showSuccessToast(productCode, quantity);
    }
  };

  // Set up event delegation for cart buttons - this runs once and handles all current and future buttons
  useEffect(() => {
    const messagesContainer = document.querySelector('.chat-content-messages');

    if (messagesContainer) {
      // Remove any existing listener first
      messagesContainer.removeEventListener('click', handleCartButtonClick);
      // Add the event listener
      messagesContainer.addEventListener('click', handleCartButtonClick);
    }

    // Cleanup function
    return () => {
      if (messagesContainer) {
        messagesContainer.removeEventListener('click', handleCartButtonClick);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);
  // Note: we intentionally remove checkContinuationPossibility from dependencies
  // and rely on conversationId directly to avoid stale closure issues

  async function savePersonalDataToFindlyCart(personalData: any) {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cart/${conversationId}/personal-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...personalData,
          lang_code: langCode,
        }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
    } catch (error) {
      console.error('Error saving personal data to Findly cart:', error);
    }
  }

  async function addProductsToFindlyCart(productCode: string, quantity: number) {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cart/${conversationId}/product`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_code: productCode,
          quantity: quantity,
          lang_code: langCode,
        }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
    } catch (error) {
      console.error('Error adding products to cart:', error);
    }
  }

  // Function to show success toast notification
  const showSuccessToast = (productCode: string, quantity: number) => {
    setToastMessage(`✅ ${t('productAddedToCart', { productCode })}`);
    setShowToast(true);

    // Auto-hide after 2.5 seconds
    setTimeout(() => {
      setShowToast(false);
      setTimeout(() => setToastMessage(null), 300); // Allow fade out animation
    }, 2500);
  };

  // Listen for streaming responses
  useEffect(() => {
    if (!conversationId) return;

    const channel = echo.channel('conversation.' + conversationId);

    channel.listen('StreamChatResponse', (data: any) => {
      // Check if it's a string or JSON object
      if (typeof data.message === 'string') {
        try {
          // Try to parse as JSON
          const jsonData = JSON.parse(data.message);

          // Check if it's a chunk format
          if (jsonData.chunk !== undefined) {
            if (jsonData.is_thinking) {
              // Update thinking step if provided and not a completion message
              if (jsonData.chunk && !jsonData.chunk.includes('✅ Step completed')) {
                setCurrentThinkingStep(jsonData.chunk);
              }

              if (jsonData.special_action === 'add_products_to_cart') {
                if (jsonData?.special_action_data?.length > 0) {
                  for (const entry of jsonData.special_action_data) {
                    console.log('Adding products to cart:', entry);
                    // addProductsToFindlyCart(entry?.product_code, entry?.quantity);
                    addProductsToWebsiteCart(entry?.product_code, entry?.quantity, entry?.special_data);
                  }
                }
              }

              if (jsonData.special_action === 'send_personal_data') {
                if (jsonData?.special_action_data) {
                  console.log('Sending personal data:', jsonData?.special_action_data);
                  savePersonalDataToFindlyCart(jsonData?.special_action_data);
                }
              }
              return;
            }
            // eslint-disable-next-line
            if ((jsonData?.final || jsonData?.is_final || jsonData?.is_content_final || jsonData?.step == 'end') && isAiTyping) {
              if (
                // eslint-disable-next-line
                currentThinkingStep == '🔍 Collecting informations' &&
                // eslint-disable-next-line
                (jsonData.chunk == '' || jsonData.chunk == ' ' || jsonData.chunk == null || !jsonData.chunk)
              ) {
                return;
              }

              // End of streaming, update messages and reset state
              const finalResponse = streamedResponse + (jsonData.chunk || '');

              // Add the final message to the messages array
              const newMessage: ChatMessage = {
                content: finalResponse,
                sender: 'AI',
                type: finalResponse.includes('<') ? 'HTML' : 'String',
              };

              // Create a new array with all existing messages plus the new one
              const updatedMessages = [...messages, newMessage];

              setMessages(updatedMessages);

              setIsAiTyping(false);
              setIsAlreadyStreaming(false);
              setStreamedResponse('');
            } else {
              // Add chunk to streaming response
              setStreamedResponse((prev) => prev + (jsonData.chunk || ''));
            }
          } else {
            // Not a chunk format, treat as regular text
            setStreamedResponse(data.message);
          }
        } catch (e) {
          // Not JSON, treat as plain text
          setStreamedResponse(data.message);
        }
      } else if (typeof data.message === 'object') {
        // Direct object - check if it's a chunk
        const jsonData = data.message;

        if (jsonData.chunk !== undefined) {
          // eslint-disable-next-line
          if (jsonData.final || jsonData.is_final || jsonData.is_content_final || (jsonData.step == 'end' && isAiTyping)) {
            if (
              // eslint-disable-next-line
              currentThinkingStep == '🔍 Collecting informations' &&
              // eslint-disable-next-line
              (jsonData.chunk == '' || jsonData.chunk == ' ' || jsonData.chunk == null || !jsonData.chunk)
            ) {
              return;
            }

            // End of streaming, update messages and reset state
            const finalResponse = streamedResponse + (jsonData.chunk || '');

            // Add the final message to the messages array
            const newMessage: ChatMessage = {
              content: finalResponse,
              sender: 'AI',
              type: finalResponse.includes('<') ? 'HTML' : 'String',
            };

            // Create a new array with all existing messages plus the new one
            const updatedMessages = [...messages, newMessage];
            setMessages(updatedMessages);
            setIsAiTyping(false);
            setStreamedResponse('');
          } else {
            // Add chunk to streaming response
            setStreamedResponse((prev) => prev + (jsonData.chunk || ''));
          }
        }
      }
    });

    return () => {
      channel.stopListening('StreamChatResponse');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, setMessages, messages, streamedResponse, isAiTyping, inputValue]);

  const handleSendMessage = () => {
    if (inputValue.trim() !== '' && !isAiTyping) {
      // Reset continuation state when sending a new message
      setCanContinueConversation(false);

      if (onSendMessage) {
        // Set AI typing state to true and reset streamed response
        setIsAiTyping(true);
        setStreamedResponse('');

        // Send the message
        onSendMessage(inputValue);
      }
      setInputValue('');

      // Focus back on input field after sending
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  };

  const scrollCarousel = (direction: 'left' | 'right') => {
    if (carouselRef.current) {
      const scrollAmount = 150; // Adjust this value as needed
      const currentScroll = carouselRef.current.scrollLeft;
      carouselRef.current.scrollTo({
        left: direction === 'left' ? currentScroll - scrollAmount : currentScroll + scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const handleChatGroupClick = (groupId: number, conversationId: number) => {
    // Reset typing states when switching chats
    setIsAiTyping(false);
    setStreamedResponse('');
    setCanContinueConversation(false);
    setIsCheckingContinuation(false);

    setCurrentChatGroupId(groupId);
    //@ts-ignore
    setConversationId(conversationId);

    setInputValue('');
    // Call parent handler to fetch messages
    if (onChatGroupChange) {
      onChatGroupChange(groupId);
    }
  };

  // Add scroll event handler for lazy loading
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const handleScroll = async () => {
      if (isLoadingMore || !hasMoreChats) return;

      const scrollPosition = carousel.scrollLeft;
      const scrollWidth = carousel.scrollWidth;
      const clientWidth = carousel.clientWidth;

      // If we're near the end (within 20% of the right edge)
      if (scrollPosition + clientWidth >= scrollWidth * 0.8) {
        setIsLoadingMore(true);
        if (onLoadMoreChats) {
          const hasMore = await onLoadMoreChats();
          setHasMoreChats(hasMore);
        }
        setIsLoadingMore(false);
      }
    };

    carousel.addEventListener('scroll', handleScroll);
    return () => carousel.removeEventListener('scroll', handleScroll);
  }, [isLoadingMore, hasMoreChats, onLoadMoreChats]);

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (document.activeElement?.tagName === 'INPUT') return;

      if (e.key === 'ArrowLeft') {
        scrollCarousel('left');
      } else if (e.key === 'ArrowRight') {
        scrollCarousel('right');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Add wheel scroll handler
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      carousel.scrollLeft += e.deltaY;
    };

    carousel.addEventListener('wheel', handleWheel, { passive: false });
    return () => carousel.removeEventListener('wheel', handleWheel);
  }, []);

  // Define the props interface for AILoaderWithFallback
  interface AiLoaderProps {
    message: string;
  }

  // Combined loader component that shows animated loader if image exists, otherwise falls back to normal loader
  const AILoaderWithFallback = ({ message }: AiLoaderProps) => {
    const [imageError, setImageError] = useState(false);
    const loadingGifUrl = getLoadingGifUrl();
    const magnifyingGlassUrl = getMagnifyingGlassUrl();

    // Show animated loader if image exists and hasn't failed
    if (loadingGifUrl && !imageError) {
      return (
        <div className="chat-message chat-message-bot animated-ai-loader" ref={isAiStreamingRef}>
          <div className="chat-message-content" style={getMessageContentStyle()}>
            <div className='ai-loader-content' style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <img
                src={loadingGifUrl}
                alt="Loading..."
                style={{
                  maxWidth: '100px',
                  maxHeight: '100px',
                  objectFit: 'contain',
                }}
                onError={() => setImageError(true)}
              />
              {message && (
                <div className='ai-loader-content-message' style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  fontSize: '13.3px',
                  fontWeight: 'bold',
                }}>
                  {magnifyingGlassUrl && (
                    <img
                      src={magnifyingGlassUrl}
                      alt="Magnifying glass"
                      style={{
                        width: '48px',
                        objectFit: 'contain',
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <span>{message}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Show fallback loader (no animated GIF or it failed to load) - use same layout as animated loader
    return (
      <div className="chat-message chat-message-bot animated-ai-loader" ref={isAiStreamingRef}>
        <div className="chat-message-content" style={getMessageContentStyle()}>
          <div className='ai-loader-content-message' style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {/* Show spinner only if no magnifying glass */}
            {!magnifyingGlassUrl && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
              }}>
                <div
                  className="findly-spinner"
                  style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid rgba(0, 0, 0, 0.1)',
                    borderRadius: '50%',
                    borderTop: `4px solid ${style.primaryColor}`,
                    animation: 'spin 0.8s linear infinite',
                  }}
                ></div>
              </div>
            )}
            {message && (
              <div className='ai-loader-content-message' style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                fontSize: '13.3px',
                fontWeight: 'bold',
              }}>
                {magnifyingGlassUrl && (
                  <img
                    src={magnifyingGlassUrl}
                    alt="Magnifying glass"
                    style={{
                      width: '48px',
                      objectFit: 'contain',
                      flexShrink: 0,
                    }}
                  />
                )}
                <span>{message}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // AI typing indicator component
  const TypingIndicator = () => (
    <div className={`chat-message-container chat-message-container-bot`}>
      <div className="chat-message-bot-image">
        <img className="chat-user-image" src={getBotImageUrl()} alt="Bot" />
      </div>
      <div className="chat-message chat-message-bot" style={getBotMessageStyle(style)}>
        <div className="chat-message-content" style={getMessageContentStyle()}>
          <div className="typing-indicator">
            <span className="dot"></span>
            <span className="dot"></span>
            <span className="dot"></span>
          </div>
        </div>
      </div>
    </div>
  );

  // Loading state for continuation check
  const ContinuationCheckLoader = () => (
    <div style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: style.primaryColor,
          fontSize: '14px',
          fontWeight: 'normal',
        }}
      >
        <div
          className="findly-spinner"
          style={{
            width: '12px',
            height: '12px',
            border: '2px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '50%',
            borderTop: `2px solid ${style.primaryColor}`,
            animation: 'spin 0.8s linear infinite',
          }}
        ></div>
        {t('checkingContinuation')}
      </div>
    </div>
  );

  // Welcome section - shown when user hasn't sent first message yet
  const WelcomeSection = () => {
    const botImageUrl = getBotImageUrl();
    const botName = getBotName();
    
    // Locale mapping for date/time formatting
    const localeMap: Record<string, string> = {
      ro: 'ro-RO',
      en: 'en-US',
      de: 'de-DE',
      fr: 'fr-FR',
      es: 'es-ES',
      it: 'it-IT'
    };
    
    const locale = localeMap[langCode] || 'en-US';
    const currentDate = new Date().toLocaleDateString(locale, { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
    const currentTime = new Date().toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        padding: '40px 20px',
        gap: '12px'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          border: `3px solid ${style.primaryColor}`,
          padding: '8px',
          backgroundColor: '#fff'
        }}>
          <img 
            src={botImageUrl} 
            alt={botName}
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              objectFit: 'cover'
            }}
          />
        </div>
        <div style={{
          textAlign: 'center',
          color: '#666',
          fontSize: '14px',
          lineHeight: '1.5'
        }}>
          <div style={{ fontWeight: 600, fontSize: '16px', color: '#333', marginBottom: '4px' }}>
            {botName}
          </div>
          <div>{t('joinedChat')}</div>
          <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
            {currentDate}, {currentTime}
          </div>
        </div>
      </div>
    );
  };

  // Helper function to get bot name from chat settings
  const getBotName = (): string => {
    try {
      const chatSettings = window.CreativeOnesChatbot?.chatSettings;
      if (chatSettings && chatSettings.success && Array.isArray(chatSettings.settings)) {
        const botNameSetting = chatSettings.settings.find((setting: any) => setting.slug === 'bot_name' && setting.is_active);
        return botNameSetting?.value || 'Findly';
      }
      return 'Findly';
    } catch (error) {
      console.error('Error getting bot name:', error);
      return 'Findly';
    }
  };

  // Check if user has sent any messages
  const hasUserSentMessage = messages.some(message => message.sender === 'user');

  return (
    <div className="chat-content" style={getChatContentStyle(style)}>
      <div className="chat-content-header" style={{ ...getHeaderStyle(style), position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingLeft: '8px' }}>
          <a href="https://thefindly.com" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Logo width="112" height="28" style={{ flexShrink: 0 }} />
          </a>
          {/* <span style={{ fontSize: '14px', fontWeight: 'bold', color: 'white' }}>
            {isConnected ? <span className="chat-connected-icon">Conectat 🟢</span> : <span className="chat-disconnected-icon">Deconectat 🔴</span>}
          </span> */}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Language Switcher - only show if supportedLanguages is provided */}
          {supportedLanguages && onLanguageChange && (
            <LanguageSwitcher
              currentLanguage={langCode}
              supportedLanguages={supportedLanguages}
              onLanguageChange={onLanguageChange}
              style={{
                primaryColor: style.primaryColor,
                backgroundColor: style.backgroundColor,
              }}
            />
          )}

          <button
            className="chat-close-btn"
            onClick={onClose}
            style={getCloseButtonStyle()}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
            }}
          >
            <CloseButtonIcon color="white" />
          </button>
        </div>
      </div>

      {/* Conversations Carousel */}
      <div className="chat-carousel-wrapper" style={{ position: 'relative' }}>
        <div className="chat-carousel-nav-left" style={getCarouselNavButtonStyle(style, 'left')} onClick={() => scrollCarousel('left')}>
          &#10094;
        </div>
        <div
          ref={carouselRef}
          className="chat-carousel-container"
          style={{
            ...getCarouselContainerStyle(style),
            cursor: 'grab',
            userSelect: 'none',
          }}
        >
          {/* eslint-disable-next-line */}
          {chatGroups.length === 0 ? (
            <button
              className="chat-carousel-item new-chat-button"
              style={{
                ...getCarouselItemStyle(style, false),
                backgroundColor: style.primaryColor,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
              onClick={() => {
                onChatGroupChange && onChatGroupChange(0);
                setInputValue('');
              }} // 0 signals to create a new chat
            >
              <span style={{ fontSize: '18.24px', fontWeight: 'bold' }}>+</span> {t('createNewChat')}
            </button>
          ) : (
            <>
              <button
                className="chat-carousel-item new-chat-button"
                style={{
                  ...getCarouselItemStyle(style, false),
                  backgroundColor: style.primaryColor,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  flexShrink: 0,
                  ...(isAiTyping && {
                    opacity: 0.6,
                    cursor: 'not-allowed',
                    transform: 'none',
                    boxShadow: 'none',
                  }),
                }}
                disabled={isAiTyping}
                onClick={() => {
                  onChatGroupChange && onChatGroupChange(0);
                  setInputValue('');
                }}
              >
                <span style={{ fontSize: '18.24px', fontWeight: 'bold' }}>+</span> {t('newConversation')}
              </button>
              {/* eslint-disable-next-line */}
              {chatGroups.map((group, index) => (
                <button
                  key={index}
                  disabled={isAiTyping}
                  className="chat-carousel-item"
                  style={{
                    ...getCarouselItemStyle(style, group.id === currentChatGroupId),
                    flexShrink: 0,
                    ...(isAiTyping && {
                      opacity: 0.6,
                      cursor: 'not-allowed',
                      transform: 'none',
                      boxShadow: 'none',
                    }),
                  }}
                  onClick={() => handleChatGroupClick(group.id, group.conversation_id)}
                >
                  {group.title || `Chat ${group.id}`}
                </button>
              ))}
              {isLoadingMore && (
                <div
                  className="chat-carousel-item"
                  style={{
                    ...getCarouselItemStyle(style, false),
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <div
                    className="findly-spinner"
                    style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(0, 0, 0, 0.1)',
                      borderRadius: '50%',
                      borderTop: `2px solid ${style.primaryColor}`,
                      animation: 'spin 0.8s linear infinite',
                    }}
                  ></div>
                </div>
              )}
            </>
          )}
        </div>
        <div className="chat-carousel-nav-right" style={getCarouselNavButtonStyle(style, 'right')} onClick={() => scrollCarousel('right')}>
          &#10095;
        </div>
      </div>

      <div className="chat-content-messages" style={getMessagesContainerStyle(style)}>
        {messages.length === 0 ? (
          <div className="chat-empty-state" style={getEmptyStateStyle()}>
            {t('noMessagesYet')}
          </div>
        ) : (
          <>
            {messages.map((message, index) => (
              <div className={`chat-message-container ${message.sender === 'user' ? 'chat-message-container-user' : 'chat-message-container-bot'}`} key={index}>
                {/* Show bot image if message is from bot */}
                {message.sender !== 'user' && (
                  <div className="chat-message-bot-image">
                    <img className="chat-user-image" src={getBotImageUrl()} alt="Bot" />
                  </div>
                )}
                <div
                  key={index}
                  className={`chat-message ${message.sender === 'user' ? 'chat-message-user' : 'chat-message-bot'}`}
                  style={message.sender === 'user' ? getUserMessageStyle(style) : getBotMessageStyle(style)}
                >
                  <div className="chat-message-content" style={getMessageContentStyle()}>
                    {renderMessageContent(message.content, message.type)}
                  </div>
                </div>

                {message.sender === 'user' && (
                  <div className="chat-message-user-image">
                    <img className="chat-user-image" src={getUserImageUrl()} alt="User" />
                  </div>
                )}
              </div>
            ))}
            {!isAlreadyStreaming && isAiTyping && streamedResponse === '' && <TypingIndicator />}
            {!isAlreadyStreaming && isAiTyping && streamedResponse !== '' && (
              <div className={`chat-message-container chat-message-container-bot`}>
                <div className="chat-message-bot-image">
                  <img className="chat-user-image" src={getBotImageUrl()} alt="Bot" />
                </div>
                <div className="chat-message chat-message-bot" style={getBotMessageStyle(style)}>
                  <div className="chat-message-content" style={getMessageContentStyle()}>
                    <div dangerouslySetInnerHTML={{ __html: streamedResponse }} />
                  </div>
                </div>
              </div>
            )}
            {!isAlreadyStreaming && isAiTyping && streamedResponse === '' && (
              <AILoaderWithFallback message={currentThinkingStep ? getThinkingStepMessage(currentThinkingStep) : loadingMessages[0]} />
            )}
            {!isAiTyping && isCheckingContinuation && <ContinuationCheckLoader />}
            {/* {!isAiTyping && !isCheckingContinuation && canContinueConversation && <ContinueConversationButton />} */}
            {isAlreadyStreaming ? <AILoaderWithFallback message={t('waitingPreviousResults')} /> : null}
            {/* Show welcome section only when user hasn't sent first message */}
            {!hasUserSentMessage && !isAiTyping && <WelcomeSection />}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-content-input" style={getInputContainerStyle(style)}>
        <input
          ref={inputRef}
          type="text"
          name="chat-input"
          id="chat-input"
          placeholder={isAiTyping ? t('inputPlaceholderGenerating') : t('inputPlaceholder')}
          className="chat-input"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && inputValue.trim() !== '' && !isAiTyping) {
              handleSendMessage();
              setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
              }, 250);
            }
          }}
          style={isInputFocused ? { ...getInputStyle(style), ...getInputFocusStyle(style) } : getInputStyle(style)}
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => setIsInputFocused(false)}
          disabled={isAiTyping || isAlreadyStreaming}
        />
        <button
          ref={sendButtonRef}
          className="chat-send-btn"
          onClick={handleSendMessage}
          disabled={inputValue.trim() === '' || isAiTyping || isAlreadyStreaming}
          style={
            isSendButtonHovered && inputValue.trim() !== '' && !isAiTyping && !isAlreadyStreaming
              ? getSendButtonHoverStyle(style)
              : getSendButtonStyle(style, inputValue.trim() === '' || isAiTyping || isAlreadyStreaming)
          }
          onMouseEnter={() => setIsSendButtonHovered(true)}
          onMouseLeave={() => setIsSendButtonHovered(false)}
        >
          <SendButtonIcon color="white" />
        </button>
      </div>
    </div>
  );
};

export default ChatContent;
